import React, { useState, useEffect } from 'react';
import KPIWidget from './KPIWidget';
import { KPIWidgetData } from './KPIWidget';
import './KPIDashboard.css';

// Types for the KPIDashboard component
export type KPIRowType = 'score' | 'trend' | 'insight';

export interface KPIRow {
  id: string;
  type: KPIRowType;
  kpis: KPIWidgetData[];
  maxItems?: number; // Optional: for trend rows (default 3), insight rows (default 1)
  showEmptyPlaceholders?: boolean; // Optional: whether to show empty placeholders
}

export interface KPIDashboardProps {
  rows: KPIRow[];
  loading?: boolean;
  error?: string | null;
  className?: string;
}

const KPIDashboard: React.FC<KPIDashboardProps> = ({ 
  rows, 
  loading = false, 
  error = null, 
  className = '' 
}) => {
  const [scrollStates, setScrollStates] = useState<Record<string, { canScrollLeft: boolean; canScrollRight: boolean }>>({});

  const handleScroll = (rowId: string, event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const canScrollLeft = target.scrollLeft > 0;
    const canScrollRight = target.scrollLeft < (target.scrollWidth - target.clientWidth - 1); // -1 for floating point precision
    
    setScrollStates((prev: Record<string, { canScrollLeft: boolean; canScrollRight: boolean }>) => ({
      ...prev,
      [rowId]: { canScrollLeft, canScrollRight }
    }));
  };

  const scrollLeft = (rowId: string) => {
    const container = document.querySelector(`[data-row-id="${rowId}"] .kpi-score-scroll-container`) as HTMLElement;
    if (container) {
      container.scrollLeft -= 240; // Scroll by one widget width + gap
    }
  };

  const scrollRight = (rowId: string) => {
    const container = document.querySelector(`[data-row-id="${rowId}"] .kpi-score-scroll-container`) as HTMLElement;
    if (container) {
      container.scrollLeft += 240; // Scroll by one widget width + gap
    }
  };

  // Initialize scroll states when rows change
  useEffect(() => {
    const initializeScrollStates = () => {
      const newScrollStates: Record<string, { canScrollLeft: boolean; canScrollRight: boolean }> = {};
      
      rows.forEach(row => {
        if (row.type === 'score') {
          const container = document.querySelector(`[data-row-id="${row.id}"] .kpi-score-scroll-container`) as HTMLElement;
          if (container) {
            const hasOverflow = container.scrollWidth > container.clientWidth;
            const canScrollLeft = container.scrollLeft > 0;
            const canScrollRight = hasOverflow && container.scrollLeft < (container.scrollWidth - container.clientWidth - 1);
            newScrollStates[row.id] = { canScrollLeft, canScrollRight };
          }
        }
      });
      
      setScrollStates(newScrollStates);
    };

    // Small delay to ensure DOM is ready
    setTimeout(initializeScrollStates, 100);
  }, [rows]);
  if (loading) {
    return (
      <div className={`kpi-dashboard ${className}`}>
        <div className="kpi-loading">Loading KPI data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`kpi-dashboard ${className}`}>
        <div className="kpi-error">{error}</div>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className={`kpi-dashboard ${className}`}>
        <div className="kpi-no-data">No KPI data available</div>
      </div>
    );
  }

  const renderRow = (row: KPIRow) => {
    const { id, type, kpis, maxItems, showEmptyPlaceholders = false } = row;
    
    // Determine max items based on row type if not specified
    const defaultMaxItems = type === 'trend' ? 3 : type === 'insight' ? 1 : undefined;
    const actualMaxItems = maxItems || defaultMaxItems;
    
    // Filter KPIs to respect max items
    const validKpis = kpis.filter(kpi => kpi && kpi.score);
    const displayKpis = actualMaxItems ? validKpis.slice(0, actualMaxItems) : validKpis;
    
    // Calculate how many empty placeholders to show
    const emptyPlaceholders = showEmptyPlaceholders && actualMaxItems 
      ? Math.max(0, actualMaxItems - displayKpis.length) 
      : 0;

    // Special handling for score row with scroll
    if (type === 'score') {
      const scrollState = scrollStates[id] || { canScrollLeft: false, canScrollRight: false };
      
      return (
        <div key={id} className={`kpi-row kpi-row-${type}`} data-row-id={id}>
          <button 
            className={`kpi-score-nav-arrow kpi-score-nav-left ${!scrollState.canScrollLeft ? 'disabled' : ''}`}
            onClick={() => scrollLeft(id)}
            disabled={!scrollState.canScrollLeft}
            title="Scroll left"
            style={{ display: scrollState.canScrollLeft || scrollState.canScrollRight ? 'flex' : 'none' }}
          />
          <div 
            className="kpi-score-scroll-container"
            onScroll={(e) => handleScroll(id, e)}
          >
            {/* Render actual KPI widgets */}
            {displayKpis.map((kpi, index) => (
              <KPIWidget 
                key={`kpi-${index}`} 
                data={kpi} 
                mode={type} 
              />
            ))}
            
            {/* Render empty placeholders if needed */}
            {Array.from({ length: emptyPlaceholders }).map((_, index) => (
              <div 
                key={`empty-${index}`} 
                className={`kpi-widget kpi-widget-${type} kpi-empty-placeholder`}
              >
                <div className="kpi-loading">No KPI data available</div>
              </div>
            ))}
          </div>
          <button 
            className={`kpi-score-nav-arrow kpi-score-nav-right ${!scrollState.canScrollRight ? 'disabled' : ''}`}
            onClick={() => scrollRight(id)}
            disabled={!scrollState.canScrollRight}
            title="Scroll right"
            style={{ display: scrollState.canScrollLeft || scrollState.canScrollRight ? 'flex' : 'none' }}
          />
        </div>
      );
    }

    return (
      <div key={id} className={`kpi-row kpi-row-${type}`}>
        {/* Render actual KPI widgets */}
        {displayKpis.map((kpi, index) => (
          <KPIWidget 
            key={`kpi-${index}`} 
            data={kpi} 
            mode={type} 
          />
        ))}
        
        {/* Render empty placeholders if needed */}
        {Array.from({ length: emptyPlaceholders }).map((_, index) => (
          <div 
            key={`empty-${index}`} 
            className={`kpi-widget kpi-widget-${type} kpi-empty-placeholder`}
          >
            <div className="kpi-loading">No KPI data available</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`kpi-dashboard ${className}`}>
      {rows.map(renderRow)}
    </div>
  );
};

export default KPIDashboard; 